import { createSlice, PayloadAction, Dispatch } from "@reduxjs/toolkit";
import { Platform } from "interfaces/db";
import { AutoDialerSetting, SelectionType, SETTING, SettingValueType, UserSelection } from "interfaces/settings";
import { getServicesManager } from "services";

// Helper functions
export function findMatchingAutodialerSetting(
    settings: AutoDialerSetting[],
    platform: Platform,
    selection: UserSelection
): AutoDialerSetting | undefined {
    return settings.find(s => {
        if (s.platform !== platform) return false;
        if (selection.type === SelectionType.ME) return s.user_id === selection.id;
        if (selection.type === SelectionType.SUB_TEAM) return s.user_group_id === selection.id;
        if (selection.type === SelectionType.OVERALL_TEAM) return s.team_id === selection.id;
        if (selection.type === SelectionType.USER) return s.locked_user_id === selection.id;
        return false;
    });
}

export function getSelectionTypeForSetting(setting: AutoDialerSetting): UserSelection {
    if (setting.locked_user_id !== "") {
        return { type: SelectionType.USER, id: setting.locked_user_id };
    }
    if (setting.user_group_id !== "") {
        return { type: SelectionType.SUB_TEAM, id: setting.user_group_id };
    }
    if (setting.team_id !== "") {
        return { type: SelectionType.OVERALL_TEAM, id: setting.team_id };
    }
    return { type: SelectionType.ME, id: setting.user_id };
}

export interface AutodialerSettingsState {
    // Original settings from the server
    savedSettings: AutoDialerSetting[];
    // Currently adjusted settings (dirty state)
    adjustedSettings: AutoDialerSetting[];
    // Currently selected platform
    selectedPlatform: Platform | null;
    selectedSectionKey: string | null;
    // Loading states
    isLoaded: boolean;
    isSaving: boolean;
    // Error state
    error: string | null;
    // Has unsaved changes
    hasUnsavedChanges: boolean;
    // Selection
    selection: UserSelection | null;
}

const initialState: AutodialerSettingsState = {
    savedSettings: [],
    adjustedSettings: [],
    selection: null,
    selectedPlatform: null,
    selectedSectionKey: null,
    isLoaded: false,
    isSaving: false,
    error: null,
    hasUnsavedChanges: false,
};

export const autodialerSettingsSlice = createSlice({
    name: 'autodialerSettings',
    initialState,
    reducers: {
        // Loading states
        setLoaded: (state, action: PayloadAction<boolean>) => {
            state.isLoaded = action.payload;
        },
        setSaving: (state, action: PayloadAction<boolean>) => {
            state.isSaving = action.payload;
        },
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },

        // Platform selection
        setSelectedPlatform: (state, action: PayloadAction<Platform | null>) => {
            state.selectedPlatform = action.payload;
        },

        // Settings management
        loadSettings: (state, action: PayloadAction<AutoDialerSetting[]>) => {
            state.savedSettings = action.payload;
            state.adjustedSettings = [...action.payload]; // Deep copy
            state.hasUnsavedChanges = false;
            state.error = null;
        },

        setSelectedSectionKey: (state, action: PayloadAction<string | null>) => {
            state.selectedSectionKey = action.payload;
        },

        updateSingleSetting: (state, action: PayloadAction<{
            platform: Platform;
            setting: SETTING;
            value: SettingValueType<SETTING> | null;
            selection: UserSelection;
        }>) => {
            const { platform, setting, value, selection } = action.payload;
            const existingSetting = findMatchingAutodialerSetting(state.adjustedSettings, platform, selection);
            const existingSettingIdx = existingSetting ? state.adjustedSettings.indexOf(existingSetting) : -1;
            
            if (existingSettingIdx === -1) {
                if (value === null) return
                const emptySetting = Object.fromEntries(Object.values(SETTING).map(setting => [setting, null])) as {[key in SETTING]: any}
                const newSetting = {
                    user_id: selection.type === SelectionType.ME ? selection.id : "",
                    user_group_id: selection.type === SelectionType.SUB_TEAM ? selection.id : "",
                    team_id: selection.type === SelectionType.OVERALL_TEAM ? selection.id : "",
                    locked_user_id: selection.type === SelectionType.USER ? selection.id : "",
                    platform,
                    ...emptySetting,
                    [setting]: value,
                } as AutoDialerSetting
                state.adjustedSettings.push(newSetting);
            }
            else { state.adjustedSettings[existingSettingIdx][setting] = value as any; }
            state.hasUnsavedChanges = true;
        },

        saveSettingsSuccess: (state) => {
            state.savedSettings = [...state.adjustedSettings];
            state.hasUnsavedChanges = false;
            state.isSaving = false;
            state.error = null;
        },

        saveSettingsFailure: (state, action: PayloadAction<string>) => {
            state.isSaving = false;
            state.error = action.payload;
        },

        resetAdjustedSettings: (state) => {
            state.adjustedSettings = [...state.savedSettings];
            state.hasUnsavedChanges = false;
        },

        clearError: (state) => {
            state.error = null;
        },

        setSelection: (state, action: PayloadAction<UserSelection | null>) => {
            state.selection = action.payload;
        },
    },
});

export const {
    setLoaded,
    setSaving,
    setError,
    setSelectedPlatform,
    loadSettings,
    setSelectedSectionKey,
    updateSingleSetting,
    saveSettingsSuccess,
    saveSettingsFailure,
    resetAdjustedSettings,
    clearError,
    setSelection,
} = autodialerSettingsSlice.actions;

// Async thunks
export const loadAutodialerSettings = (dispatch: Dispatch) => {
    return getServicesManager().getAllAutodialerSettings()
        .then(settings => {
            if (!settings) {
                dispatch(setLoaded(false))
                dispatch(setError('Failed to load settings'))
                return
            }
            // get a count of settings for each platform
            const platforms = new Set(settings.map(s => s.platform));
            if (platforms.size === 1) dispatch(setSelectedPlatform(Array.from(platforms)[0]));
            const platformCounts = new Map(Array.from(platforms, p => [p, settings.filter(s => s.platform === p).length]));
            if (platformCounts.size > 1) {
                const platformsSorted = Array.from(platformCounts.entries()).sort((a, b) => b[1] - a[1])
                if (platformsSorted[0][1] > 2 * platformsSorted[1][1]) {
                    dispatch(setSelectedPlatform(platformsSorted[0][0]));
                }
            }
            dispatch(loadSettings(settings))
            dispatch(setLoaded(true))
        })
        .catch(error => {
            dispatch(setLoaded(false))
            dispatch(setError(error.message || 'Failed to load settings'))
        })
};

export const autodialerSettingsReducer = autodialerSettingsSlice.reducer; 